/**
 * Signals a disallowed module specifier so callers can hide the detail from end
 * users. Kept out of `compile` so the host page doesn't bundle the SDK and sucrase.
 */
export class IllegalImportError extends Error {}

export const CANVAS_RENDER_STAGES = ["compile", "runtime"] as const;
export type CanvasRenderStage = (typeof CANVAS_RENDER_STAGES)[number];

export type CanvasRenderError = {
  stage: CanvasRenderStage;
  message: string;
  /** Detail is hidden from end users when set; analytics still records the category. */
  illegalImport: boolean;
};

export type CanvasRenderErrorHandler = (error: CanvasRenderError) => void;

export function isCanvasRenderStage(
  value: unknown,
): value is CanvasRenderStage {
  return (CANVAS_RENDER_STAGES as readonly unknown[]).includes(value);
}

export function toCanvasRenderError(
  error: unknown,
  stage: CanvasRenderStage,
): CanvasRenderError {
  return {
    stage,
    message: error instanceof Error ? error.message : String(error),
    illegalImport: error instanceof IllegalImportError,
  };
}

/** Validates the error fields of an untrusted record, e.g. a `canvas:error` frame message. */
export function parseCanvasRenderError(
  record: Record<string, unknown>,
): CanvasRenderError | null {
  return isCanvasRenderStage(record.stage) && typeof record.message === "string"
    ? {
        stage: record.stage,
        message: record.message,
        illegalImport: record.illegalImport === true,
      }
    : null;
}
